import {
  ListLocationsCommand,
  type ListLocationsCommandInput,
  type ListLocationsCommandOutput,
} from '@amzn/gbsa-helios-typescript-client';
import {
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';
import { apiHeliosClient } from '../index';
import { v2LocationKeys } from './keys';

/**
 * v2 React Query hook for listing locations.
 *
 * Locations are scoped per business unit. The query stays idle until a
 * `businessUnitId` is supplied — callers gate on the active BU so we
 * don't fire a wildcard request on first paint.
 *
 * @param input - List filters. `businessUnitId` is required for the query
 *                to enable; pass `isEnabledForBU: true` to limit results
 *                to locations the BU has activated.
 * @param options - React Query overrides. `queryKey` and `queryFn` are
 *                  owned by the hook and cannot be supplied.
 */
export const useLocationsQuery = <TData = ListLocationsCommandOutput>(
  input: Partial<ListLocationsCommandInput>,
  options?: Omit<
    UseQueryOptions<ListLocationsCommandOutput, Error, TData>,
    'queryKey' | 'queryFn'
  >,
): UseQueryResult<TData> =>
  useQuery({
    ...options,
    queryKey: v2LocationKeys.list(input),
    enabled: (options?.enabled ?? true) && !!input.businessUnitId,
    queryFn: () => {
      if (!input.businessUnitId) {
        throw new Error('businessUnitId is required to list locations');
      }
      return apiHeliosClient.send(
        new ListLocationsCommand({
          ...input,
          businessUnitId: input.businessUnitId,
        }),
      );
    },
  });
